// Live updates over WebSocket (ADR 0040).
//
// One socket per tab while signed in. The server sends "something
// changed" events; we mark the matching TanStack queries stale and they
// refetch through the normal API. Polling stays as the fallback, so a
// dropped socket only makes updates slower.
//
// - ``useLiveUpdates()`` (in the app shell) owns the socket.
// - ``useLiveWatch(fileId)`` asks for a file's comment events while a
//   component showing them is mounted.
// - ``{"type": "files"}`` (ADR 0051): something you can see changed;
//   file listings, search results and the activity feed refetch (only
//   the ones on screen; the rest are marked stale).

import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { api } from "@/api/client";
import { activityKeys } from "@/api/activity";
import { commentKeys } from "@/api/comments";
import { fileKeys } from "@/api/queries";
import { useAuth } from "@/auth/stub";

interface LiveEvent {
  type: string;
  file_id?: string;
  channel?: string;
}

// fileId -> how many mounted components watch it.
const watched = new Map<string, number>();
let socket: WebSocket | null = null;

// WebSocket.OPEN, without needing WebSocket to exist (tests, old browsers).
const OPEN = 1;

function send(message: object): void {
  if (socket?.readyState === OPEN) {
    socket.send(JSON.stringify(message));
  }
}

// A sync client uploading a folder sends one event per file: refetch at
// most once per FILES_THROTTLE_MS, and once more after a burst ends.
export const FILES_THROTTLE_MS = 2000;
let filesTimer: ReturnType<typeof setTimeout> | null = null;
let filesPending = false;

function refetchFiles(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: fileKeys.all });
  void qc.invalidateQueries({ queryKey: activityKeys.all });
}

function filesChanged(qc: QueryClient): void {
  if (filesTimer !== null) {
    filesPending = true;
    return;
  }
  refetchFiles(qc);
  const tick = () => {
    if (filesPending) {
      filesPending = false;
      refetchFiles(qc);
      filesTimer = setTimeout(tick, FILES_THROTTLE_MS);
    } else {
      filesTimer = null;
    }
  };
  filesTimer = setTimeout(tick, FILES_THROTTLE_MS);
}

function handle(qc: QueryClient, event: LiveEvent): void {
  if (event.type === "files") {
    filesChanged(qc);
  } else if (event.type === "notification") {
    void qc.invalidateQueries({ queryKey: ["notifications"] });
  } else if (event.type === "comments" && event.file_id) {
    void qc.invalidateQueries({ queryKey: commentKeys.file(event.file_id) });
  }
}

/** Refetch what may have changed while the socket was down. */
function catchUp(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: ["notifications"] });
  refetchFiles(qc);
  for (const fileId of watched.keys()) {
    void qc.invalidateQueries({ queryKey: commentKeys.file(fileId) });
  }
}

async function openSocket(): Promise<WebSocket | null> {
  const { data } = await api.POST("/api/v1/live/ticket", {});
  if (!data) return null;
  const scheme = window.location.protocol === "https:" ? "wss" : "ws";
  return new WebSocket(
    `${scheme}://${window.location.host}/api/v1/live?ticket=${encodeURIComponent(data.ticket)}`,
  );
}

/** Keep the live socket open while signed in; reconnect with backoff. */
export function useLiveUpdates(): void {
  const qc = useQueryClient();
  const userId = useAuth((s) => s.user?.id ?? null);

  useEffect(() => {
    if (userId === null || typeof WebSocket === "undefined") return;
    let stopped = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let delay = 1000;
    let everOpen = false;

    const connect = async () => {
      let ws: WebSocket | null = null;
      try {
        ws = await openSocket();
      } catch {
        ws = null;
      }
      if (stopped) {
        ws?.close();
        return;
      }
      if (ws === null) {
        schedule();
        return;
      }
      socket = ws;
      ws.onopen = () => {
        delay = 1000;
        for (const fileId of watched.keys()) {
          send({ op: "watch", file_id: fileId });
        }
        if (everOpen) catchUp(qc);
        everOpen = true;
      };
      ws.onmessage = (e: MessageEvent<string>) => {
        try {
          handle(qc, JSON.parse(e.data) as LiveEvent);
        } catch {
          // Not JSON: ignore.
        }
      };
      ws.onclose = () => {
        if (socket === ws) socket = null;
        if (!stopped) schedule();
      };
    };

    const schedule = () => {
      retry = setTimeout(() => void connect(), delay);
      delay = Math.min(delay * 2, 30_000);
    };

    void connect();
    return () => {
      stopped = true;
      clearTimeout(retry);
      socket?.close();
      socket = null;
    };
  }, [qc, userId]);
}

/** Get live comment events for ``fileId`` while mounted. */
export function useLiveWatch(fileId: string): void {
  useEffect(() => {
    const n = watched.get(fileId) ?? 0;
    watched.set(fileId, n + 1);
    if (n === 0) send({ op: "watch", file_id: fileId });
    return () => {
      const left = (watched.get(fileId) ?? 1) - 1;
      if (left > 0) {
        watched.set(fileId, left);
      } else {
        watched.delete(fileId);
        send({ op: "unwatch", file_id: fileId });
      }
    };
  }, [fileId]);
}
